import { useEffect, useRef, useState } from "react";
import { Mic, Pause, Play, RotateCcw, Square, Trash2 } from "lucide-react";

const MAX_FRAGMENT_MS = 15 * 60 * 1000;
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"];

function formatTime(milliseconds) {
  const seconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function VoiceRecorder({ onFragmentsChange, resetToken = 0, disabled = false, confirmRestart = null, showRestart = true }) {
  const [fragments, setFragments] = useState([]);
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const accumulatedRef = useRef(0);

  const publish = (next) => {
    setFragments(next);
    onFragmentsChange?.(next);
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => () => {
    releaseStream();
    fragments.forEach((item) => URL.revokeObjectURL(item.url));
  }, []); // URLs are also revoked explicitly on removal/reset.

  useEffect(() => {
    releaseStream();
    fragments.forEach((item) => URL.revokeObjectURL(item.url));
    setFragments([]);
    onFragmentsChange?.([]);
    setState("idle");
    setElapsed(0);
  }, [resetToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state !== "recording") return undefined;
    const timer = window.setInterval(() => {
      const current = accumulatedRef.current + Date.now() - startedAtRef.current;
      setElapsed(current);
      if (current >= MAX_FRAGMENT_MS) {
        accumulatedRef.current = MAX_FRAGMENT_MS;
        recorderRef.current?.stop();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [state]);

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Запись голоса не поддерживается этим браузером.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MIME_CANDIDATES.find((value) => MediaRecorder.isTypeSupported(value)) || "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      accumulatedRef.current = 0;
      startedAtRef.current = Date.now();
      setElapsed(0);
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        const durationMs = Math.min(MAX_FRAGMENT_MS, accumulatedRef.current);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const next = [...fragments, { id: crypto.randomUUID(), blob, url: URL.createObjectURL(blob), durationMs }];
        publish(next);
        releaseStream();
        setState("idle");
        setElapsed(0);
      };
      recorder.start(500);
      setState("recording");
    } catch (_error) {
      releaseStream();
      setError("Не удалось получить доступ к микрофону.");
    }
  };

  const pause = () => {
    if (recorderRef.current?.state !== "recording") return;
    accumulatedRef.current += Date.now() - startedAtRef.current;
    recorderRef.current.pause();
    setElapsed(accumulatedRef.current);
    setState("paused");
  };

  const resume = () => {
    if (recorderRef.current?.state !== "paused") return;
    startedAtRef.current = Date.now();
    recorderRef.current.resume();
    setState("recording");
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      if (state === "recording") accumulatedRef.current += Date.now() - startedAtRef.current;
      recorderRef.current.stop();
    }
  };

  const remove = (id) => {
    const item = fragments.find((fragment) => fragment.id === id);
    if (item) URL.revokeObjectURL(item.url);
    publish(fragments.filter((fragment) => fragment.id !== id));
  };

  return (
    <section className="rounded-3xl border border-[#E7DAC6] bg-white p-5 sm:p-7" data-testid="voice-recorder">
      <div className="flex flex-wrap items-center gap-3">
        {state === "idle" && <button type="button" disabled={disabled} onClick={start} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#4A3728] px-5 text-sm text-[#FFF8ED]"><Mic size={17} />{fragments.length ? "Добавить ещё голосом" : "Начать запись"}</button>}
        {state === "recording" && <><button type="button" onClick={pause} className="inline-flex min-h-12 items-center gap-2 rounded-xl border border-[#D8C7AE] px-4 text-sm"><Pause size={17} />Пауза</button><button type="button" onClick={stop} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#7E3F35] px-4 text-sm text-white"><Square size={16} />Закончить</button></>}
        {state === "paused" && <><button type="button" onClick={resume} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#4A3728] px-4 text-sm text-white"><Play size={17} />Продолжить</button><button type="button" onClick={stop} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#7E3F35] px-4 text-sm text-white"><Square size={16} />Закончить</button></>}
        {state !== "idle" && <span className="font-mono text-sm text-[#6F5B45]">{formatTime(elapsed)} / 15:00</span>}
      </div>
      {error && <p className="mt-3 text-sm text-[#9A3F35]" role="alert">{error}</p>}
      <div className="mt-5 space-y-3">
        {fragments.map((fragment, index) => <div key={fragment.id} className="flex flex-col gap-3 rounded-2xl bg-[#F8F1E7] p-4 sm:flex-row sm:items-center">
          <div className="min-w-32 text-sm text-[#3B2A1E]">Фрагмент {index + 1} · {formatTime(fragment.durationMs)}</div>
          <audio className="h-10 min-w-0 flex-1" controls preload="metadata" src={fragment.url}>Прослушать запись</audio>
          <button type="button" onClick={() => remove(fragment.id)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#D8C7AE] px-3 text-sm"><Trash2 size={15} />Удалить</button>
        </div>)}
      </div>
      {showRestart && fragments.length > 0 && state === "idle" && <button type="button" onClick={() => {
        if (confirmRestart && !confirmRestart()) return;
        fragments.forEach((item) => URL.revokeObjectURL(item.url));
        publish([]);
      }} className="mt-4 inline-flex min-h-11 items-center gap-2 text-sm text-[#765D44] underline"><RotateCcw size={15} />Начать заново</button>}
    </section>
  );
}
